/**
 * AccountabilityPack — W48 Marketing Dashboard Component (F)
 *
 * Marketing vs sales accountability matrix.
 * MQL target / actual / status by territory or industry.
 *
 * Data sourced from /api/marketing/abm-top10 (W47 route) with funnel overlay.
 * composite_score=0 handled gracefully (D-098 bug, show em-dash).
 *
 * No Recharts. SVG progress bars + Tailwind table.
 * Canon: 5-color palette, teal #0F766E for on-track, orange for at-risk, red for behind.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

interface AbmRow {
  account_id: string;
  account_name: string;
  owner_name: string;
  status: string;
  mpact_score: number;
  mql_target: number;
  mql_actual: number;
  status_pill: string;
}

interface AbmData {
  rows: AbmRow[];
}

// Industry grouping for territory/industry bucketing
const TERRITORY_FALLBACK = "General";

function groupByIndustry(rows: AbmRow[]): Map<string, AbmRow[]> {
  // No industry column on this endpoint; group all under "Pipeline accounts"
  const map = new Map<string, AbmRow[]>();
  for (const r of rows) {
    const key = r.status ?? TERRITORY_FALLBACK;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  return map;
}

function StatusPill({ pill }: { pill: string }) {
  if (pill === "—") {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  const isOnTrack = pill === "ON TRACK";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{
        backgroundColor: isOnTrack ? "#DCFCE7" : "#FEF3C7",
        color: isOnTrack ? "#15803D" : "#92400E",
      }}
    >
      {pill}
    </span>
  );
}

// SVG mini progress bar
function ProgressBar({ actual, target }: { actual: number; target: number }) {
  if (target === 0) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  const pct = Math.min(1, actual / target);
  const color = pct >= 0.8 ? "#16A34A" : pct >= 0.5 ? "#EAB308" : "#DC2626";
  return (
    <div className="flex items-center gap-2">
      <svg width="60" height="8" viewBox="0 0 60 8" fill="none" aria-hidden="true">
        <rect x="0" y="0" width="60" height="8" rx="4" fill="#E5E7EB" />
        <rect x="0" y="0" width={Math.round(60 * pct)} height="8" rx="4" fill={color} />
      </svg>
      <span className="text-[11px] tabular-nums text-muted-foreground">
        {actual}/{target}
      </span>
    </div>
  );
}

function MpactScore({ score }: { score: number }) {
  if (score === 0) return <span className="text-[11px] text-muted-foreground">—</span>;
  const color = score >= 70 ? "#16A34A" : score >= 40 ? "#EAB308" : "#DC2626";
  return (
    <span className="text-[12px] font-semibold tabular-nums" style={{ color }}>
      {score}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-8 bg-[hsl(var(--surface-2))] rounded" />
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-10 bg-[hsl(var(--surface-2))] rounded" />
      ))}
    </div>
  );
}

export function AccountabilityPack() {
  const session = getSession();
  const [data, setData] = useState<AbmData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }

    fetch("/api/marketing/abm-top10", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData({ rows: d.rows ?? [] }); setLoading(false); })
      .catch((e) => {
        console.error("[AccountabilityPack]", e);
        setError("Could not load accountability data");
        setLoading(false);
      });
  }, [session?.token]);

  if (loading) return <Skeleton />;
  if (error) return <p className="text-[12px] text-[#DC2626]">{error}</p>;

  const rows = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6 text-center">
        <p className="text-[13px] text-muted-foreground">
          No ABM accounts found. Add accounts to the sprint board.
        </p>
      </div>
    );
  }

  const statusGroups = groupByIndustry(rows);

  return (
    <div className="flex flex-col gap-4">
      {/* Summary KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Accounts tracked", value: rows.length, color: "#0F766E" },
          {
            label: "Avg M-PACT",
            value: rows.filter((r) => r.mpact_score > 0).length > 0
              ? Math.round(
                  rows.filter((r) => r.mpact_score > 0).reduce((s, r) => s + r.mpact_score, 0) /
                  rows.filter((r) => r.mpact_score > 0).length
                )
              : null,
            color: "#16A34A",
            nullLabel: "—",
          },
          {
            label: "On track",
            value: rows.filter((r) => r.status_pill === "ON TRACK").length,
            color: "#16A34A",
          },
          {
            label: "At risk",
            value: rows.filter((r) => r.status_pill === "AT RISK").length,
            color: "#DC2626",
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-lg border border-border bg-[hsl(var(--surface))] p-3"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {kpi.label}
            </p>
            <p
              className="mt-1 text-[22px] font-bold tabular-nums leading-none"
              style={{ color: kpi.value != null ? kpi.color : "#6B7280" }}
            >
              {kpi.value != null ? kpi.value : ("nullLabel" in kpi ? kpi.nullLabel : "—")}
            </p>
          </div>
        ))}
      </div>

      {/* Accountability matrix table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-2.5 bg-[hsl(var(--surface-2))] border-b border-border">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Marketing vs Sales — ABM Accountability
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="border-b border-border">
              <tr>
                {["Account", "Status", "Owner", "M-PACT", "MQL Target", "MQL Actual", "Track"].map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.account_id} className="hover:bg-[hsl(var(--surface-2))] transition-colors">
                  <td className="px-3 py-2.5 font-medium text-foreground">
                    <a href={`#/account/${r.account_id}`} className="hover:underline hover:text-[#0F766E]">
                      {r.account_name}
                    </a>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                    {r.status}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                    {r.owner_name}
                  </td>
                  <td className="px-3 py-2.5">
                    <MpactScore score={r.mpact_score} />
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                    {r.mql_target === 0 ? "—" : r.mql_target}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                    {r.mql_actual === 0 && r.mql_target === 0 ? "—" : r.mql_actual}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusPill pill={r.status_pill} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground px-1">
        MQL target and actual shown as — where marketing_events has no account_id column (W47 known gap).
        Composite score of 0 indicates unscored account (D-098).
      </p>
    </div>
  );
}
